"use client";

import { useDesignTheme } from "@/components/design-theme-provider";
import { useBackgroundMode } from "@/components/background-provider";
import { designThemes, DesignTheme } from "@/lib/design-themes";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Palette, Moon, Sun, Monitor, Zap } from "lucide-react";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { trackThemeChange } from "@/lib/analytics";

export function DesignThemeSwitcher() {
  const { designTheme, setDesignTheme } = useDesignTheme();
  const { backgroundMode, setBackgroundMode } = useBackgroundMode();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [bottomOffset, setBottomOffset] = useState(24);

  /* eslint-disable @eslint-react/set-state-in-effect */
  useEffect(() => {
    setMounted(true);
  }, []);
  /* eslint-enable @eslint-react/set-state-in-effect */

  useEffect(() => {
    const footer = document.querySelector("footer");
    if (!footer) return;

    const update = () => {
      const visible = window.innerHeight - footer.getBoundingClientRect().top;
      setBottomOffset(visible > 0 ? visible + 24 : 24);
    };

    window.addEventListener("scroll", update, { passive: true });
    update();
    return () => window.removeEventListener("scroll", update);
  }, [mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger asChild aria-label="Change theme">
        <button
          style={{ bottom: bottomOffset }}
          className="fixed right-6 z-50 size-14 rounded-full bg-background shadow-lg border flex items-center justify-center glow-hover outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-2 focus-visible:outline-transparent print:hidden"
          data-faro-user-action-name="open-theme-switcher"
        >
          <Palette className="h-5 w-5" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent side="top" align="end" className="w-80 p-3" aria-label="Theme settings">
        <div className="space-y-4">
          <div>
            <div id="brightness-label" className="font-semibold text-sm mb-2">Brightness</div>
            <div className="flex gap-2" role="group" aria-labelledby="brightness-label">
              <Button
                variant={mounted && theme === "light" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                aria-pressed={mounted && theme === "light"}
                data-faro-user-action-name="theme-brightness-light"
                onClick={() => {
                  trackThemeChange("brightness", "light", theme);
                  setTheme("light");
                }}
              >
                <Sun className="h-4 w-4 mr-2" aria-hidden="true" />
                Light
              </Button>
              <Button
                variant={mounted && theme === "dark" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                aria-pressed={mounted && theme === "dark"}
                data-faro-user-action-name="theme-brightness-dark"
                onClick={() => {
                  trackThemeChange("brightness", "dark", theme);
                  setTheme("dark");
                }}
              >
                <Moon className="h-4 w-4 mr-2" aria-hidden="true" />
                Dark
              </Button>
              <Button
                variant={mounted && theme === "system" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                aria-pressed={mounted && theme === "system"}
                data-faro-user-action-name="theme-brightness-system"
                onClick={() => {
                  trackThemeChange("brightness", "system", theme);
                  setTheme("system");
                }}
              >
                <Monitor className="h-4 w-4 mr-2" aria-hidden="true" />
                Auto
              </Button>
            </div>
          </div>

          <div className="border-t pt-3">
            <div id="animation-label" className="font-semibold text-sm mb-2">Animation</div>
            <div className="flex gap-2" role="group" aria-labelledby="animation-label">
              <Button
                variant={backgroundMode === "static" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                aria-pressed={backgroundMode === "static"}
                data-faro-user-action-name="theme-animation-static"
                onClick={() => {
                  trackThemeChange("animation", "static", backgroundMode);
                  setBackgroundMode("static");
                }}
              >
                Pause
              </Button>
              <Button
                variant={backgroundMode === "animated" ? "default" : "outline"}
                size="sm"
                className="flex-1"
                aria-pressed={backgroundMode === "animated"}
                data-faro-user-action-name="theme-animation-animated"
                onClick={() => {
                  trackThemeChange("animation", "animated", backgroundMode);
                  setBackgroundMode("animated");
                }}
              >
                <Zap className="h-4 w-4 mr-2" aria-hidden="true" />
                Play
              </Button>
            </div>
          </div>

          <div className="border-t pt-3">
            <div id="design-theme-label" className="font-semibold text-sm mb-2">Design Theme</div>
            <div className="grid grid-cols-2 gap-2" role="group" aria-labelledby="design-theme-label">
              {(Object.keys(designThemes) as DesignTheme[]).map((themeKey) => (
                <Button
                  key={themeKey}
                  variant={designTheme === themeKey ? "default" : "outline"}
                  size="sm"
                  className="flex-1"
                  aria-pressed={designTheme === themeKey}
                  data-faro-user-action-name={`theme-design-${themeKey}`}
                  onClick={() => {
                    trackThemeChange("design", themeKey, designTheme);
                    setDesignTheme(themeKey);
                  }}
                  aria-label={`Select ${designThemes[themeKey].name} theme`}
                >
                  {designThemes[themeKey].name}
                </Button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
